import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { getUserIntegrationsStatus } from './userIntegrationService';
import { idTokenHeader } from './authHeaders';

// Google OAuth tokens live in server-only storage. Every Google Calendar call
// goes through a Cloud Function; the browser never sees or refreshes a token.

/**
 * Fetch Google Calendar events for a user
 * @param {string} userId - The user ID
 * @param {Date} startDate - Start date for the event range
 * @param {Date} endDate - End date for the event range
 * @returns {Promise<Array>} Array of Google Calendar events
 */
export const fetchGoogleCalendarEvents = async (userId, startDate, endDate) => {
  try {
    const response = await fetch('https://us-central1-bba-portal-894c2.cloudfunctions.net/fetchGoogleCalendarEvents', {
      method: 'POST',
      headers: {
        ...(await idTokenHeader()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      })
    });

    let result = {};
    try {
      result = await response.json();
    } catch {
      /* Non-JSON error body */
    }

    const calendarDisconnected =
      result && result.connected === false && result.success !== true;

    // Legacy deployments: HTTP 400/404 when calendar disconnected
    if (!response.ok && calendarDisconnected) {
      return [];
    }

    if (!response.ok) {
      throw new Error(`Cloud Function error: ${response.status} ${response.statusText}`);
    }

    if (result.success) {
      return result.events || [];
    }
    // Expected for teammates without CRM calendar connected — no noisy error
    if (calendarDisconnected) {
      return [];
    }
    if (result.error) console.warn('fetchGoogleCalendarEvents:', result.error);
    return [];
  } catch (error) {
    console.error('Error calling fetchGoogleCalendarEvents function:', error);
    return [];
  }
};

/**
 * Create a Google Calendar event
 * @param {string} userId - The user ID
 * @param {Object} eventData - Event data to create
 * @returns {Promise<Object>} Created Google Calendar event
 */
export const createGoogleCalendarEvent = async (userId, eventData) => {
  try {
    const response = await fetch('https://us-central1-bba-portal-894c2.cloudfunctions.net/createGoogleCalendarEvent', {
      method: 'POST',
      headers: {
        ...(await idTokenHeader()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        eventData
      })
    });

    let result = {};
    try {
      result = await response.json();
    } catch {
      /* Non-JSON error body */
    }

    if (response.ok && result.success) {
      return result.googleEvent;
    }

    throw new Error(result.error || `Failed to create Google Calendar event (${response.status})`);
  } catch (error) {
    console.error('Error calling createGoogleCalendarEvent function:', error);
    throw error;
  }
};

/**
 * Check if user has Google Calendar integration connected
 * @param {string} userId - The user ID
 * @returns {Promise<boolean>} True if connected, false otherwise
 */
export const isGoogleCalendarConnected = async (userId) => {
  try {
    const integrationsStatus = await getUserIntegrationsStatus(userId);
    return integrationsStatus.googleCalendar?.connected === true;
  } catch (error) {
    console.error('Error checking Google Calendar connection:', error);
    return false;
  }
};

/**
 * Refresh Google Calendar access token (server-side; no token is returned)
 * @param {string} userId - The user ID
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const refreshGoogleCalendarToken = async (userId) => {
  try {
    const response = await fetch('https://us-central1-bba-portal-894c2.cloudfunctions.net/refreshGoogleCalendarToken', {
      method: 'POST',
      headers: {
        ...(await idTokenHeader()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId })
    });

    const result = await response.json();
    return result.success || false;
  } catch (error) {
    console.error('Error refreshing Google Calendar token:', error);
    return false;
  }
};

/**
 * Update a Google Calendar event on the signed-in user's own calendar.
 * @param {string} userId - Unused; the server always updates the caller's calendar
 * @param {string} eventId - Google Calendar event ID
 * @param {Object} eventData - Updated event data
 * @returns {Promise<Object>} Updated Google Calendar event
 */
export const updateGoogleCalendarEvent = async (userId, eventId, eventData) => {
  try {
    const callable = httpsCallable(functions, 'updateGoogleCalendarEvent');
    const { data } = await callable({ eventId, eventData });
    if (!data?.success) {
      throw new Error(data?.error || 'Failed to update Google Calendar event');
    }
    return data.event;
  } catch (error) {
    console.error('Error updating Google Calendar event:', error);
    throw error;
  }
};
